<template>
  <div class="manual">
    <aside class="toc">
      <div class="toc-title">目录</div>
      <a
        v-for="s in sections"
        :key="s.id"
        :class="{ active: activeId === s.id }"
        href="javascript:;"
        @click="scrollTo(s.id)"
      >{{ s.title }}</a>
    </aside>
    <div class="manual-body" ref="body">
      <div class="intro-box">
        <h2>用户操作手册</h2>
        <p>按客户经营上手顺序编写，覆盖 PC 管理端与销售 App。建议首次使用时按章节顺序操作一遍。</p>
        <div class="quick">
          <div class="quick-title">快速上手（半天跑通主线）</div>
          <ol>
            <li>PC：公司信息与用户角色 → 维护产品与扩展字段（可选）</li>
            <li>PC：录入线索 → 跟进 → 转成客户与商机</li>
            <li>PC：推进商机 → 报价审批 → 签合同 → 建回款计划</li>
            <li>App：工作台看待办，外勤领取公海线索并跟进</li>
            <li>主管：审批报价，关注逾期回款与公海回收</li>
          </ol>
        </div>
      </div>

      <section v-for="s in sections" :id="s.id" :key="s.id" class="chapter">
        <h3>{{ s.title }}</h3>
        <div v-for="(block, bi) in s.blocks" :key="bi" class="block">
          <h4 v-if="block.heading">{{ block.heading }}</h4>
          <p v-if="block.path" class="path">菜单位置：{{ block.path }}</p>
          <p v-if="block.text" class="text">{{ block.text }}</p>
          <ol v-if="block.steps && block.steps.length">
            <li v-for="(t, i) in block.steps" :key="'s' + i">{{ t }}</li>
          </ol>
          <ul v-if="block.bullets && block.bullets.length">
            <li v-for="(t, i) in block.bullets" :key="'b' + i">{{ t }}</li>
          </ul>
          <p v-if="block.tip" class="tip">提示：{{ block.tip }}</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
const sections = [
  {
    id: 'ch1',
    title: '一、认识产品与账号',
    blocks: [
      {
        heading: '1.1 产品定位',
        text: '华锐CRM 面向销售团队的客户经营：线索、客户、商机、报价、合同、回款一条线，支持公海、跟进提醒与审批。'
      },
      {
        heading: '1.2 如何进入',
        bullets: [
          'PC：浏览器打开管理端地址，搜索企业后登录',
          'App：PC 登录页扫描「扫码下载 App」，按提示安装',
          '新企业可在登录页注册，短信验证码开通免费版'
        ]
      },
      {
        heading: '1.3 账号类型',
        bullets: [
          '管理员：组织、套餐、全部菜单',
          '销售：线索、客户、商机跟进（通常仅本人数据）',
          '销售主管：本部门数据与审批',
          '客服：客户服务、提醒相关能力'
        ],
        tip: '首次登录 PC 需搜索选择企业；忘记密码可用登录页短信重置。'
      }
    ]
  },
  {
    id: 'ch2',
    title: '二、开户与组织准备（PC）',
    blocks: [
      {
        heading: '2.1 公司信息',
        path: '系统管理 → 公司信息',
        steps: [
          '上传 Logo，填写公司名称、联系人、电话、地址等并保存',
          '填写发票信息（抬头、税号等），申请开票时自动带出'
        ]
      },
      {
        heading: '2.2 部门、角色、用户',
        path: '系统管理 → 部门管理 / 角色管理 / 用户管理',
        text: '建议顺序：部门 → 角色勾选菜单与数据范围 → 用户分配角色。',
        bullets: [
          '部门：新增编码、名称、上级后保存',
          '角色：勾选可见菜单；数据范围可选本人 / 本部门 / 全部',
          '用户：选部门、角色，设置登录账号后保存；可重置密码'
        ]
      },
      {
        heading: '2.3 套餐订阅',
        path: '套餐订阅',
        text: '查看当前套餐、账号上限；可在线支付或对公转账认款升级。',
        tip: '到期后可登录续费，业务数据变为只读直到续订。'
      }
    ]
  },
  {
    id: 'ch3',
    title: '三、线索与公海',
    blocks: [
      {
        heading: '3.1 录入与跟进线索',
        path: '客户管理 → 线索',
        steps: [
          '新增线索：姓名、电话、公司、来源、意向等',
          '写跟进记录（通话 / 企微 / 备注），必要时建跟进提醒',
          '确认有价值后点「转商机」，系统会创建或关联客户并生成商机'
        ],
        tip: '保存前注意查重，避免同一电话或公司重复建档。'
      },
      {
        heading: '3.2 公海',
        path: '客户管理 → 公海',
        bullets: [
          '主动「放入公海」或超时未跟进后进入公海',
          '销售可领取公海线索 / 客户到自己名下',
          '公海数据不属于个人私有，便于重新分配'
        ]
      }
    ]
  },
  {
    id: 'ch4',
    title: '四、客户与联系人',
    blocks: [
      {
        heading: '4.1 客户档案',
        path: '客户管理 → 客户',
        steps: [
          '维护客户名称、行业、等级、地址等',
          '在客户下挂联系人、跟进记录、关联商机',
          '长期无人跟进可放入客户公海，或由主管转交给接手人'
        ]
      },
      {
        heading: '4.2 联系人',
        path: '客户管理 → 联系人',
        text: '记录客户侧决策人、使用人等角色与电话，跟进时方便对口。'
      }
    ]
  },
  {
    id: 'ch5',
    title: '五、商机、报价与合同',
    blocks: [
      {
        heading: '5.1 商机推进',
        path: '销售管理 → 商机',
        steps: [
          '商机关联客户，填写标题、金额、赢率、预计成交日',
          '按阶段推进：接触 → 需求 → 方案 → 赢单 / 丢单',
          '过程跟进记在跟进记录里，便于复盘'
        ]
      },
      {
        heading: '5.2 产品与报价',
        path: '业务配置 → 产品；销售管理 → 报价',
        steps: [
          '先维护产品目录与标准价',
          '对商机新建报价，可挂多行明细与折扣',
          '需要审批的报价提交后，在「审批」中通过或驳回'
        ]
      },
      {
        heading: '5.3 合同与订单',
        path: '销售管理 → 合同 / 销售订单',
        text: '报价通过后签合同；合同生效后可生成销售订单，用于履约跟踪。'
      }
    ]
  },
  {
    id: 'ch6',
    title: '六、回款、发票与审批',
    blocks: [
      {
        heading: '6.1 回款计划',
        path: '财务台账 → 回款',
        steps: [
          '按合同拆分期次与到期日',
          '到账后标记已回款；逾期项在工作台可见'
        ]
      },
      {
        heading: '6.2 销售发票',
        path: '财务台账 → 销售发票',
        text: '记录对客户开具的销售发票；与「套餐订阅」里的平台开票申请不是同一回事。'
      },
      {
        heading: '6.3 审批',
        path: '协同与智能 → 审批',
        text: '处理待审报价、合同等单据，留下通过 / 驳回意见。'
      }
    ]
  },
  {
    id: 'ch7',
    title: '七、提醒、扩展字段与助手',
    blocks: [
      {
        heading: '7.1 跟进提醒',
        path: '客户管理 → 跟进提醒',
        text: '为线索、客户、商机建待办，到期在工作台提醒，避免断跟。'
      },
      {
        heading: '7.2 扩展字段',
        path: '业务配置 → 扩展字段',
        text: '在线索 / 客户 / 商机 / 合同上增加扩展字段，不修改系统字段名称。'
      },
      {
        heading: '7.3 经营助手',
        path: '协同与智能 → 经营助手',
        text: '配置可用时，可协助查客户概况、记跟进、评估商机；未配置模型时不影响其余功能。'
      }
    ]
  },
  {
    id: 'ch8',
    title: '八、App 与常见问题',
    blocks: [
      {
        heading: '8.1 销售 App',
        bullets: [
          '工作台：线索数、公海、商机、待办、待审批',
          '线索：查看、领取公海、放入公海、转商机',
          '客户：查看名下与公海客户'
        ]
      },
      {
        heading: '8.2 常见问题',
        bullets: [
          '看不到别人的客户：角色数据范围可能是「本人」或「本部门」',
          '套餐页没有某套餐：运维可能已在「套餐上架」中下架',
          '验证码收不到：开发环境为模拟短信，验证码固定 123456',
          '有问题：顶栏「咨询工单」提交，平台会邮件通知客服（若已开启邮件通知）'
        ]
      }
    ]
  }
]

export default {
  name: 'HelpUserManual',
  data() {
    return {
      sections,
      activeId: 'ch1'
    }
  },
  mounted() {
    const q = this.$route.query.section
    if (q && sections.some(s => s.id === q)) {
      this.$nextTick(() => this.scrollTo(q))
    }
  },
  methods: {
    scrollTo(id) {
      this.activeId = id
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.manual {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  max-width: 1100px;
}
.toc {
  position: sticky;
  top: 0;
  flex: 0 0 168px;
  max-height: calc(100vh - 160px);
  overflow: auto;
  padding: 12px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.toc-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
  padding: 0 6px;
}
.toc a {
  display: block;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
  text-decoration: none;
  line-height: 1.4;
  margin-bottom: 2px;
}
.toc a:hover { background: #f1f5f9; color: #0f172a; }
.toc a.active {
  background: #ecfdf5;
  color: #0f766e;
  font-weight: 600;
}
.manual-body { flex: 1; min-width: 0; }
.intro-box {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 14px;
}
.intro-box h2 {
  margin: 0 0 6px;
  font-size: 20px;
  color: #0f172a;
}
.intro-box > p {
  margin: 0 0 14px;
  color: #64748b;
  font-size: 14px;
}
.quick {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.quick-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}
.quick ol {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}
.chapter {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px 8px;
  margin-bottom: 12px;
  scroll-margin-top: 12px;
}
.chapter h3 {
  margin: 0 0 12px;
  font-size: 16px;
  color: #0f172a;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}
.block { margin-bottom: 14px; }
.block h4 {
  margin: 0 0 6px;
  font-size: 14px;
  color: #0f172a;
}
.path {
  margin: 0 0 6px;
  font-size: 12px;
  color: #0f766e;
  font-weight: 600;
}
.text {
  margin: 0 0 6px;
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
}
.block ol, .block ul {
  margin: 0 0 6px;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}
.tip {
  margin: 6px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 12px;
  line-height: 1.5;
}
@media (max-width: 900px) {
  .manual { flex-direction: column; }
  .toc {
    position: static;
    flex: none;
    width: 100%;
    max-height: none;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .toc-title { width: 100%; }
  .toc a { display: inline-block; }
}
</style>
